import React, { useState, useEffect } from "react";
import { Box, Stack, Button, Typography, Autocomplete, TextField, Chip, Pagination, Alert, Paper } from "@mui/material";
import SimpleFormDialog, { SimpleFormLabel, SimpleFormTextField } from "components/dialogs/SimpleFormDialog";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import TopBar from "components/dashboardParts/TopBar";
import { setDocumentTitle } from "system/Utilities";
import APIRequest, { APIError } from "system/APIRequest";
import { InfegyUser } from "system/InfegyUser";

import Team, { TeamType, TeamMember } from "./Team";

export default function TeamsPage(): React.JSX.Element {
  const { i18n } = useTranslation();

  // Data State
  const [teams, setTeams] = useState<TeamType[]>([]);
  const [sharedTeams, setSharedTeams] = useState<TeamType[]>([]);
  const [users, setUsers] = useState<InfegyUser[]>([]);

  // Form State
  const [teamID, setTeamID] = useState<string>("");
  const [teamName, setTeamName] = useState<string>("");
  const [teamDescription, setTeamDescription] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<InfegyUser[]>([]);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  // Dialog State
  const [createTeamDialog, setCreateTeamDialog] = useState(false);
  const [editTeamDialog, setEditTeamDialog] = useState(false);
  const [addTeamMemberDialog, setAddTeamMemberDialog] = useState<boolean>(false);
  const [removeTeamMemberDialog, setRemoveTeamMemberDialog] = useState<boolean>(false);
  const [deleteTeamDialog, setDeleteTeamDialog] = useState(false);

  // Pagination State
  const [teamPage, setTeamPage] = useState(1);
  const [sharedTeamPage, setSharedTeamPage] = useState(1);
  const pageSize = 5; 
  const paginatedTeams = teams.slice((teamPage - 1) * pageSize, teamPage * pageSize);
  const paginatedSharedTeams = sharedTeams.slice((sharedTeamPage - 1) * pageSize, sharedTeamPage * pageSize);
  const handleTeamPageChange = (_event:React.ChangeEvent<unknown>, value:number) => setTeamPage(value);
  const handleSharedTeamPageChange = (_event:React.ChangeEvent<unknown>, value:number) => setSharedTeamPage(value);

  useEffect(() => {
    setDocumentTitle(i18n.t("teams.document-title"));
    fetchTeams();
    fetchSharedTeams();
    fetchUsers();
  }, []);

  function fetchTeams() {
    APIRequest("obj/team", {}, { method: "GET" }).then(async (response) => {
      const teamsRaw = response?.output || [];
      const teamsWithMembers = await Promise.all(teamsRaw.map((team:TeamType) => fetchMembers(team)));
      setTeams(teamsWithMembers);
    }).catch((error: APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-fetching-teams")}: ${error}`, { variant: "error" });
      setTeams([]);
    });
  }

  function fetchSharedTeams() {
    APIRequest("me/teams", {}, { method: "GET" }).then(async (response) => {
      const sharedTeamsRaw = response?.teams.filter((team:TeamType) => !team._owned_by_you) || [];
      const sharedTeamsWithMembers = await Promise.all(sharedTeamsRaw.map((team:TeamType) => fetchMembers(team)));
      setSharedTeams(sharedTeamsWithMembers);
    }).catch((error: APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-fetching-shared-teams")}: ${error}`, { variant: "error" });
      setSharedTeams([]);
    })
  }

  async function fetchMembers(team:TeamType) {
    try {
      const members = await APIRequest(`obj/team/${team.id}/entry`, {}, { method: "GET" });
      return { ...team, members: members.output || [] };
    } catch (error) {
      enqueueSnackbar(`${i18n.t("teams.errors.error-fetching-team-members")}: ${error}`, { variant: "error" });
      return { ...team, members: [] };
    }
  }

  function fetchUsers() {
    APIRequest("share-targets", {}, { method: "GET" }).then((response) => {
      setUsers(response.user);
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-fetching-share-targets")}: ${error}`, { variant: "error" });
    });
  }

  function createTeam(teamName:string, teamDescription:string) {
    APIRequest("obj/team", {}, {
      method: "POST",
      body: JSON.stringify({ name: teamName, description: teamDescription })
    }).then((response) => { 
      if (selectedUsers.length > 0) {
        addTeamMember(response.id, selectedUsers)
      } else {
        fetchTeams();
        clearFields();
      }
      setCreateTeamDialog(false);
      enqueueSnackbar(i18n.t("teams.actions.team-created", { teamName: teamName }), { variant: "success" });
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-creating-team")}: ${error}`, { variant: "error" });
    });
  }

  function editTeam(teamID:string, teamName:string, teamDescription:string) {
    APIRequest(`obj/team/${teamID}`, {}, {
      method: "PUT",
      body: JSON.stringify({ name: teamName, description: teamDescription })
    }).then(() => {
      fetchTeams();
      clearFields();
      setEditTeamDialog(false);
      enqueueSnackbar(i18n.t("teams.actions.team-edited", { teamName: teamName }), { variant: "success" });
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-editing-team")}: ${error}`, { variant: "error" });
    });
  }

  function addTeamMember(teamID:string, selectedUsers:InfegyUser[]) {
    Promise.all(selectedUsers.map((user) => {
      APIRequest("obj/team-member", {}, { 
        method: "POST", 
        body: JSON.stringify({ team_id: teamID, user_id: user.id }), 
      });
    })).then(() => {
      fetchTeams();
      clearFields();
      setAddTeamMemberDialog(false);
      enqueueSnackbar(i18n.t("teams.actions.added-team-member", { teamName: teamName }), { variant: "success" });
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-adding-team-member")}: ${error}`, { variant: "error" });
    })
  }

  function removeTeamMember(member:TeamMember) {
    APIRequest(`obj/team-member/${member.id}`, {}, { method: "DELETE" }).then(() => { 
      fetchTeams();
      clearFields();
      setRemoveTeamMemberDialog(false);
      enqueueSnackbar(i18n.t("teams.actions.removed-team-member", {
        teamName: teamName,
        teamMember: selectedMember ? users.find((user) => 
          user.id === selectedMember.user_id)?.name : i18n.t("teams.dialogs.remove-member.unknown-member")
      }), { variant: "success" });
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-removing-team-member")}: ${error}`, { variant: "error" });
    });
  }

  function deleteTeam(teamID:string) { 
    APIRequest(`obj/team/${teamID}`, {}, { method: "DELETE" }).then(() => {
      fetchTeams();
      clearFields();
      setDeleteTeamDialog(false);
      enqueueSnackbar(i18n.t("teams.actions.team-deleted", { teamName: teamName }), { variant: "success" });
    }).catch((error:APIError) => {
      enqueueSnackbar(`${i18n.t("teams.errors.error-deleting-team")}: ${error}`, { variant: "error" });
    });
  }
  
  const handleEditTeam = (teamID:string) => {
    setTeam(teamID);
    setEditTeamDialog(true);
  }

  const handleAddMember = (teamID:string) => {
    setTeam(teamID);
    setAddTeamMemberDialog(true);
  }

  const handleRemoveMember = (teamID:string, selectedMember:TeamMember) => {
    setTeam(teamID);
    setSelectedMember(selectedMember);
    setRemoveTeamMemberDialog(true);
  }

  const handleDeleteTeam = (teamID:string) => {
    setTeam(teamID);
    setDeleteTeamDialog(true);
  }

const setTeam = (teamID: string) => {
  const team = teams.find(t => t.id === teamID) || sharedTeams.find(t => t.id === teamID);

  if (!team) return;

  setTeamID(team.id);
  setTeamName(team.name || "");
  setTeamDescription(team.description || "");
};

  const clearFields = () => {
    setTeamID("");
    setTeamName("");
    setTeamDescription("");
    setSelectedUsers([]);
    setSelectedMember(null);
  }

  const renderTeams = paginatedTeams.map(team => (
    <Team key={team.id}
      team={team}
      users={users}
      onEditTeam={handleEditTeam}
      onDeleteTeam={handleDeleteTeam}
      onAddMember={handleAddMember}
      onRemoveMember={handleRemoveMember}
    />
  ));

  const renderSharedTeams = paginatedSharedTeams.map(team => (
    <Team key={team.id}
      team={team}
      users={users}
      onEditTeam={handleEditTeam}
      onDeleteTeam={handleDeleteTeam}
      onAddMember={handleAddMember}
      onRemoveMember={handleRemoveMember}
    />
  ));

  return (
    <Box sx={{ maxWidth: "1300px", mx: "auto", px: 2, position: "relative" }}>
      <TopBar showLogo={true} title={i18n.t("teams.document-title")}/>

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button color="success" onClick={() => setCreateTeamDialog(true)} variant="contained">
          {i18n.t("teams.actions.create-team")}
        </Button>
      </Box>

      {/* No Teams */}
      {teams.length === 0 &&
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Stack
            direction="column"
            sx={{
              gap: 2,
              p: 2
            }}>
            <Alert severity="info">{i18n.t("teams.no-teams-description")}</Alert>
          </Stack>
        </Box>
      }

      {/* Teams */}
      {teams.length > 0 &&
        <Box>
          <Stack sx={{ mb: 2 }}>
            <Typography variant="h3">{i18n.t("teams.teams-header")}</Typography>
            <Typography variant="body1">{i18n.t("teams.teams-description")}</Typography>
          </Stack>
          {renderTeams}
          {teams.length > pageSize &&
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.ceil(teams.length / pageSize)}
                page={teamPage}
                shape={"rounded"}
                onChange={handleTeamPageChange}
              />
            </Box>
          }
        </Box>
      }

      {/* Shared Teams */}
      {sharedTeams.length > 0 &&
        <Box>
          <Stack sx={{ mb: 2 }}>
            <Typography variant="h3">{i18n.t("teams.shared-teams-header")}</Typography>
            <Typography variant="body1">{i18n.t("teams.shared-teams-description")}</Typography>
          </Stack>
          {renderSharedTeams}
          {sharedTeams.length > pageSize &&
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Pagination
                count={Math.ceil(sharedTeams.length / pageSize)}
                page={sharedTeamPage}
                shape={"rounded"}
                onChange={handleSharedTeamPageChange}
              />
            </Box>
          }
        </Box>
      }

      {/* Create Team */}
      <SimpleFormDialog
        isOpen={createTeamDialog}
        title={i18n.t("teams.dialogs.create-team.title")}
        confirmText={i18n.t("create")}
        confirmColor={"success"}
        onCancel={() => { setCreateTeamDialog(false); clearFields(); }}
        onConfirm={() => createTeam(teamName, teamDescription)}
      >
        <Paper sx={{ p: 2, pt: 0.5 }}>
        <SimpleFormLabel>{i18n.t("teams.dialogs.create-team.name")}</SimpleFormLabel>
        <SimpleFormTextField autoFocus required
          id="team-name"
          name="team-name"
          value={teamName}
          placeholder={i18n.t("teams.dialogs.create-team.name-placeholder")}
          onChange={(e) => setTeamName(e.target.value)}
        />
        <SimpleFormLabel>{i18n.t("teams.dialogs.create-team.description")}</SimpleFormLabel>
        <SimpleFormTextField
          id="team-description"
          name="team-description"
          value={teamDescription}
          placeholder={i18n.t("teams.dialogs.create-team.description-placeholder")}
          onChange={(e) => setTeamDescription(e.target.value)}
        />
        <SimpleFormLabel>{i18n.t("teams.dialogs.add-member.description", { teamName: "" })}</SimpleFormLabel>
        <Autocomplete multiple openOnFocus autoComplete autoHighlight disableCloseOnSelect
          options={users}
          value={selectedUsers}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(e, newValue) => { setSelectedUsers(newValue) }}
          renderInput={(params) =>  
            <TextField {...params} variant="outlined" label={i18n.t("teams.dialogs.add-member.label")} /> 
          }
          renderValue={(value, getItemProps) => value.map((option, index) => 
            <Chip label={option.name} {...getItemProps({ index })} key={option.id} /> )
          }
        />
        </Paper>
      </SimpleFormDialog>

      {/* Edit Team */}
      <SimpleFormDialog
        isOpen={editTeamDialog}
        title={i18n.t("teams.dialogs.edit-team.title")}
        onCancel={() => { setEditTeamDialog(false); clearFields(); }}
        onConfirm={() => editTeam(teamID, teamName, teamDescription)}
      >
        <Paper sx={{ p: 2, pt: 0.5 }}>
        <SimpleFormLabel>{i18n.t("teams.dialogs.create-team.name")}</SimpleFormLabel>
        <SimpleFormTextField autoFocus required
          id="team-name"
          name="team-name"
          value={teamName}
          placeholder={i18n.t("teams.dialogs.create-team.name-placeholder")}
          onChange={(e) => setTeamName(e.target.value)}
        />
        <SimpleFormLabel>{i18n.t("teams.dialogs.create-team.description")}</SimpleFormLabel>
        <SimpleFormTextField
          id="team-description"
          name="team-description"
          value={teamDescription}
          placeholder={i18n.t("teams.dialogs.create-team.description-placeholder")}
          onChange={(e) => setTeamDescription(e.target.value)}
        />
        </Paper>
      </SimpleFormDialog>

      {/* Add Team Member */}
      <SimpleFormDialog
        isOpen={addTeamMemberDialog}
        title={i18n.t("teams.dialogs.add-member.title")}
        onCancel={() => { setAddTeamMemberDialog(false); clearFields(); }}
        onConfirm={() => addTeamMember(teamID, selectedUsers)}
      >
        <Paper sx={{ p: 2, pt: 0.5 }}>
        <SimpleFormLabel>{i18n.t("teams.dialogs.add-member.description", { teamName: teamName })}</SimpleFormLabel>
        <Autocomplete multiple openOnFocus autoComplete autoHighlight disableCloseOnSelect
          options={users}
          value={selectedUsers}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(e, newValue) => { setSelectedUsers(newValue) }}
          renderInput={(params) =>  
            <TextField {...params} variant="outlined" label={i18n.t("teams.dialogs.add-member.label")} /> 
          }
          renderValue={(value, getItemProps) => value.map((option, index) => 
            <Chip label={option.name} {...getItemProps({ index })} key={option.id} /> )
          }
        />
        </Paper>
      </SimpleFormDialog>

      {/* Remove Team Member */}
      <SimpleFormDialog
        isOpen={removeTeamMemberDialog}
        title={i18n.t("teams.dialogs.remove-member.title")}
        confirmText={i18n.t("remove")}
        confirmColor={"error"}
        onCancel={() => { setRemoveTeamMemberDialog(false); clearFields(); }}
        onConfirm={() => removeTeamMember(selectedMember!)} 
      >
        <Paper sx={{ p: 2, pt: 0.5 }}>
        {i18n.t("teams.dialogs.remove-member.description", {
          teamName: teamName,
          teamMember: selectedMember ? users.find((user) => 
            user.id === selectedMember.user_id)?.name : i18n.t("teams.dialogs.remove-member.unknown-member") 
        })}
        </Paper>
      </SimpleFormDialog>

      {/* Delete Team */}
      <SimpleFormDialog
        isOpen={deleteTeamDialog}
        title={i18n.t("teams.dialogs.delete-team.title")}
        confirmText={i18n.t("delete")}
        confirmColor={"error"}
        onCancel={() => { setDeleteTeamDialog(false); clearFields(); }}
        onConfirm={() => deleteTeam(teamID)}
      >
        <Paper sx={{ p: 2, pt: 0.5 }}>
        {i18n.t("teams.dialogs.delete-team.description", { teamName: teamName })}
        </Paper>
      </SimpleFormDialog>
    </Box>
  );
}
